import React, { useState, useEffect } from 'react';
import './Login.css';
import collegeConfig from '../../config/collegeConfig';
import { MdEmail, MdVisibility, MdVisibilityOff, MdSecurity, MdWifiOff } from 'react-icons/md';
import { RiLockPasswordFill } from 'react-icons/ri';
import { Capacitor } from '@capacitor/core';
import StudentRegistration from '../StudentRegistration/StudentRegistration';
import AppDownloadBanner from '../AppDownloadBanner/AppDownloadBanner';
import DemoVideoPlayer from '../DemoVideoPlayer/DemoVideoPlayer';

// Detect if running as installed PWA or Capacitor native app
const isNativePlatform = Capacitor.isNativePlatform();
const nativePlatform   = Capacitor.getPlatform(); // 'android', 'ios', or 'web'
const isStandalonePWA  = window.matchMedia('(display-mode: standalone)').matches ||
                         window.navigator.standalone === true;


const Login = ({ onNavigate, onViewPrivacy, isOnline = true }) => {
  // Safe initialization with fallback
  const initialUserType = collegeConfig && collegeConfig.userTypes && collegeConfig.userTypes[0]
    ? collegeConfig.userTypes[0].id
    : 'student';

  const [userType, setUserType] = useState(initialUserType);
  const [showRegistration, setShowRegistration] = useState(false);
  const [paymentMode, setPaymentMode] = useState('institute'); // optimistic default matches server default
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [focusedField, setFocusedField] = useState('');
  const [error, setError] = useState('');

  const [showForgotModal, setShowForgotModal] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotStep, setForgotStep] = useState(1); // 1 = enter email, 2 = enter OTP
  const [forgotOTP, setForgotOTP] = useState('');
  const [forgotResult, setForgotResult] = useState(null); // { ok: bool, message: string }

  const showInstallOptions =
    !isNativePlatform && !isStandalonePWA && nativePlatform === 'web' && paymentMode === 'institute';

  useEffect(() => {
    // Add fade-in animation on component mount
    const loginContainer = document.querySelector('.login-container');
    if (loginContainer) {
      loginContainer.classList.add('fade-in');
    }

    // Check payment mode to decide whether to show "Purchase" button
    fetch(`${collegeConfig.api.baseURL}/payment/settings`)
      .then(r => r.json())
      .then(data => {
        if (data.success) setPaymentMode(data.settings.paymentMode);
      })
      .catch(() => {});
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (error) {
      setError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${collegeConfig.api.baseURL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Store token in localStorage
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        
        
        // Navigate to appropriate dashboard based on user role
        if (onNavigate) {
          onNavigate(data.user.role);
        } else {
          // Fallback navigation if onNavigate is not provided
          switch(data.user.role) {
            case 'admin':
              window.location.href = '/admin-dashboard';
              break;
            case 'teacher':
              window.location.href = '/teacher-dashboard';
              break;
            case 'student':
              window.location.href = '/student-dashboard';
              break;
            default:
              console.error('Unknown user role:', data.user.role);
          }
        }
      } else {
        // Display the specific error message from the backend
        setError(data.message || 'Login failed. Please try again.');
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Login error:', error);
      setError('Unable to connect to server. Please try again later.');
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    setForgotResult(null);
    try {
      const response = await fetch(`${collegeConfig.api.baseURL}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail })
      });
      const data = await response.json();
      if (response.ok) {
        setForgotStep(2);
      } else {
        setForgotResult({ ok: false, message: data.message });
      }
    } catch {
      setForgotResult({ ok: false, message: 'Unable to connect to server. Please try again.' });
    } finally {
      setForgotLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    setForgotResult(null);
    try {
      const response = await fetch(`${collegeConfig.api.baseURL}/auth/verify-reset-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail, otp: forgotOTP })
      });
      const data = await response.json();
      setForgotResult({ ok: response.ok, message: data.message });
    } catch {
      setForgotResult({ ok: false, message: 'Unable to connect to server. Please try again.' });
    } finally {
      setForgotLoading(false);
    }
  };

  const closeForgotModal = () => {
    setShowForgotModal(false);
    setForgotEmail('');
    setForgotOTP('');
    setForgotStep(1);
    setForgotResult(null);
  };

  const handleUserTypeChange = (type) => {
    setUserType(type);
    // Reset form when switching user type
    setFormData({ email: '', password: '' });
    setError(''); // Clear any errors
  };

  // If config is not loaded, show a loading state or error
  if (!collegeConfig) {
    return (
      <div className="login-container">
        <div className="login-card">
          <p>Loading configuration...</p>
        </div>
      </div>
    );
  }

  // Show student self-registration flow
  if (showRegistration) {
    return (
      <StudentRegistration
        onBack={() => setShowRegistration(false)}
        onSuccess={() => setShowRegistration(false)}
      />
    );
  }

  return (
    <div className="login-container">
      <div className="login-background">
        <div className="animated-bg-element element-1"></div>
        <div className="animated-bg-element element-2"></div>
        <div className="animated-bg-element element-3"></div>
      </div>

      <div className="login-card">
        <div className="login-header">
          {/* Logo from config */}
          <img 
            src={collegeConfig.logo} 
            alt={`${collegeConfig.shortName} Logo`} 
            className="logo" 
          />
          
          <h1 className="hospital-name">{collegeConfig.collegeName}</h1>
          <h2 className="app-title">{collegeConfig.appName}</h2>
        </div>

        {/* User Type Selector - Only show if multiple user types */}
        {collegeConfig.userTypes && collegeConfig.userTypes.length > 1 && (
          <div className="user-type-selector">
            {collegeConfig.userTypes.map((type) => (
              <button
                key={type.id}
                type="button"
                className={`user-type-btn ${userType === type.id ? 'active' : ''}`}
                onClick={() => handleUserTypeChange(type.id)}
              >
                <span className="icon">{type.icon}</span>
                <span>{type.label}</span>
              </button>
            ))}
          </div>
        )}

        {/* Offline notice */}
        {!isOnline && (
          <div style={{
            background: '#fff3cd',
            color: '#856404',
            border: '1px solid #ffc107',
            padding: '10px 14px',
            borderRadius: '8px',
            marginBottom: '15px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '13px'
          }}>
            <MdWifiOff size={16} />
            <span>You're offline. Connect to the internet to sign in.</span>
          </div>
        )}

        {/* Error message display */}
        {error && (
          <div style={{
            background: '#fee',
            color: '#c33',
            padding: '10px',
            borderRadius: '8px',
            marginBottom: '15px',
            textAlign: 'center',
            fontSize: '14px'
          }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="login-form">
          <div className={`form-group0 ${focusedField === 'email' ? 'focused' : ''}`}>
            <div className="input-wrapper">
              <span className="input-icon">
                <MdEmail size={20} />
              </span>
              <input
                type="email"
                id="email"
                name="email"
                autoComplete="email"
                value={formData.email}
                onChange={handleInputChange}
                onFocus={() => setFocusedField('email')}
                onBlur={() => setFocusedField('')}
                placeholder="Enter your email"
                required
                className="form-input"
              />
            </div>
          </div>

          <div className={`form-group0 ${focusedField === 'password' ? 'focused' : ''}`}>
            <div className="input-wrapper">
              <span className="input-icon">
                <RiLockPasswordFill size={20} />
              </span>
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                autoComplete="current-password"
                value={formData.password}
                onChange={handleInputChange}
                onFocus={() => setFocusedField('password')}
                onBlur={() => setFocusedField('')}
                placeholder="Enter your password"
                required
                className="form-input"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? (
                  <MdVisibilityOff size={20} />
                ) : (
                  <MdVisibility size={20} />
                )}
              </button>
            </div>
          </div>

          <div className="form-options">
            {collegeConfig.features && collegeConfig.features.rememberMe && (
              <label className="remember-me">
                <input type="checkbox" />
                <span>Remember me</span>
              </label>
            )}
            {collegeConfig.features && collegeConfig.features.forgotPassword && (
              <button type="button" className="forgot-password" onClick={() => setShowForgotModal(true)}>
                Forgot Password?
              </button>
            )}
          </div>

          <button
            type="submit"
            className={`login-btn ${isLoading ? 'loading' : ''}`}
            disabled={isLoading || !isOnline}
          >
            {isLoading ? (
              <>
                <span className="spinner1"></span>
                Signing in...
              </>
            ) : (
              `Sign in`
            )}
          </button>
        </form>
        {paymentMode === 'student' && !isNativePlatform && (
          <div style={{ textAlign: 'center', marginTop: '16px' }}>
            <div style={{
              borderTop: '1px solid var(--border-color)',
              paddingTop: '16px',
              marginTop: '4px'
            }}>
              <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '6px' }}>
                New student? Purchase your e-logbook access
              </p>
              <p style={{ fontSize: '12px', color: 'var(--text-light)', marginBottom: '10px' }}>
                Already have an account? Sign in above — your renewal option will appear after login.
              </p>
              <button
                type="button"
                onClick={() => setShowRegistration(true)}
                style={{
                  width: '100%',
                  padding: '12px',
                  background: 'transparent',
                  border: '2px solid var(--primary-color)',
                  borderRadius: '8px',
                  color: 'var(--primary-color)',
                  fontWeight: '700',
                  fontSize: '14px',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
                onMouseOver={e => {
                  e.currentTarget.style.background = 'var(--primary-color)';
                  e.currentTarget.style.color = 'white';
                }}
                onMouseOut={e => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = 'var(--primary-color)';
                }}
              >
                🎓 Purchase E-Logbook
              </button>
            </div>
          </div>
        )}

        {/* App Download — hidden when running as PWA or native app, or when student pays */}
        {showInstallOptions && <AppDownloadBanner />}

        {/* Demo video */}
        {collegeConfig.demoVideos?.login && (
          <div style={{ textAlign: 'center', margin: '14px 0 4px' }}>
            <DemoVideoPlayer src={collegeConfig.demoVideos.login} label="Watch Demo" />
          </div>
        )}

        <div className="login-footer">
          <p className="help-text">
            Need help? Contact Admin | {' '}
            <button
              type="button"
              className="email-link"
              onClick={() => window.location.href = `mailto:${collegeConfig.supportEmail}`}
            >
              {collegeConfig.supportEmail}
            </button>
          </p>
          <p className="version-text">
            Version {collegeConfig.version} | © {collegeConfig.copyrightYear} {collegeConfig.shortName}
          </p>
          <div style={{ marginTop: '10px' }}>
            <button
              type="button"
              className="privacy-policy-btn"
              onClick={onViewPrivacy}
            >
              <MdSecurity size={14} />
              Privacy Policy
            </button>
          </div>
        </div>
      </div>
      {/* Forgot Password Modal */}
      {showForgotModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 99999, padding: '20px'
        }}>
          <div style={{
            background: '#fff', borderRadius: '16px', width: '100%', maxWidth: '420px',
            padding: '36px 32px', boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
            animation: 'slideUp 0.25s ease-out'
          }}>
            <h2 style={{ margin: '0 0 8px', fontSize: '20px', color: '#1f2933', fontWeight: 700 }}>
              {forgotStep === 1 ? 'Reset Password' : 'Enter Verification Code'}
            </h2>
            <p style={{ margin: '0 0 24px', fontSize: '14px', color: '#4a5568', lineHeight: 1.6 }}>
              {forgotStep === 1
                ? 'Enter your registered email address. We will send a 6-digit verification code.'
                : `A 6-digit code was sent to ${forgotEmail}. Enter it below to reset your password.`}
            </p>

            {forgotResult ? (
              <>
                <div style={{
                  padding: '16px', borderRadius: '10px', marginBottom: '24px',
                  background: forgotResult.ok ? '#e8f5e9' : '#ffebee',
                  border: `1px solid ${forgotResult.ok ? '#a5d6a7' : '#ef9a9a'}`,
                  color: forgotResult.ok ? '#2e7d32' : '#c62828',
                  fontSize: '14px', lineHeight: 1.6, textAlign: 'center'
                }}>
                  {forgotResult.ok ? '✅ ' : '❌ '}{forgotResult.message}
                </div>
                {forgotResult.ok && (
                  <p style={{ margin: '0 0 20px', fontSize: '13px', color: '#4a5568', textAlign: 'center' }}>
                    📬 Don't see it? Check your <strong>Spam</strong> or <strong>Junk</strong> folder and mark it as "Not Spam".
                  </p>
                )}
                {!forgotResult.ok && forgotStep === 2 && (
                  <button type="button" onClick={() => { setForgotResult(null); setForgotOTP(''); }} style={{
                    width: '100%', marginBottom: '10px', padding: '11px',
                    background: '#f4f6f9', color: '#4a5568',
                    border: '1px solid #e0e0e0', borderRadius: '8px',
                    fontSize: '14px', fontWeight: 600, cursor: 'pointer'
                  }}>
                    Try Again
                  </button>
                )}
                <button onClick={closeForgotModal} style={{
                  width: '100%', padding: '12px',
                  background: 'linear-gradient(135deg, var(--primary-color), var(--primary-color-light))',
                  color: '#fff', border: 'none', borderRadius: '8px',
                  fontSize: '15px', fontWeight: 600, cursor: 'pointer'
                }}>
                  {forgotResult.ok ? 'Done' : 'Close'}
                </button>
              </>
            ) : forgotStep === 1 ? (
              <form onSubmit={handleForgotPassword}>
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: 600, color: '#1f2933' }}>
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    placeholder="Enter your registered email"
                    required
                    style={{
                      width: '100%', padding: '11px 14px', fontSize: '14px',
                      border: '2px solid #e0e0e0', borderRadius: '8px',
                      outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.2s'
                    }}
                    onFocus={(e) => e.target.style.borderColor = 'var(--primary-color)'}
                    onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                  />
                </div>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <button type="button" onClick={closeForgotModal} style={{
                    flex: 1, padding: '12px', background: '#f4f6f9',
                    color: '#4a5568', border: '1px solid #e0e0e0', borderRadius: '8px',
                    fontSize: '14px', fontWeight: 600, cursor: 'pointer'
                  }}>
                    Cancel
                  </button>
                  <button type="submit" disabled={forgotLoading} style={{
                    flex: 2, padding: '12px',
                    background: forgotLoading ? '#ccc' : 'linear-gradient(135deg, var(--primary-color), var(--primary-color-light))',
                    color: '#fff', border: 'none', borderRadius: '8px',
                    fontSize: '14px', fontWeight: 600, cursor: forgotLoading ? 'not-allowed' : 'pointer'
                  }}>
                    {forgotLoading ? 'Sending...' : 'Send Code'}
                  </button>
                </div>
              </form>
            ) : (
              <form onSubmit={handleVerifyOTP}>
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: 600, color: '#1f2933' }}>
                    6-Digit Code
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]{6}"
                    maxLength={6}
                    value={forgotOTP}
                    onChange={(e) => setForgotOTP(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="Enter 6-digit code"
                    required
                    autoFocus
                    style={{
                      width: '100%', padding: '11px 14px', fontSize: '22px',
                      letterSpacing: '6px', textAlign: 'center', fontFamily: 'monospace',
                      border: '2px solid #e0e0e0', borderRadius: '8px',
                      outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.2s'
                    }}
                    onFocus={(e) => e.target.style.borderColor = 'var(--primary-color)'}
                    onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                  />
                </div>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <button type="button" onClick={() => { setForgotStep(1); setForgotOTP(''); }} style={{
                    flex: 1, padding: '12px', background: '#f4f6f9',
                    color: '#4a5568', border: '1px solid #e0e0e0', borderRadius: '8px',
                    fontSize: '14px', fontWeight: 600, cursor: 'pointer'
                  }}>
                    Back
                  </button>
                  <button type="submit" disabled={forgotLoading || forgotOTP.length !== 6} style={{
                    flex: 2, padding: '12px',
                    background: (forgotLoading || forgotOTP.length !== 6) ? '#ccc' : 'linear-gradient(135deg, var(--primary-color), var(--primary-color-light))',
                    color: '#fff', border: 'none', borderRadius: '8px',
                    fontSize: '14px', fontWeight: 600, cursor: (forgotLoading || forgotOTP.length !== 6) ? 'not-allowed' : 'pointer'
                  }}>
                    {forgotLoading ? 'Verifying...' : 'Verify & Reset'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;